/**
 * Tienda y apertura de sobres.
 *
 * La apertura es la única parte del juego cuyo valor es puramente teatral: el
 * servidor ya decidió las cartas antes de que se mueva un píxel. Aun así importa
 * — abrir un sobre es el momento que la gente recuerda — así que se revelan una
 * a una y las raras se hacen esperar.
 *
 * Lo que no se hace: adivinar el resultado ni animar nada antes de tenerlo. Si
 * la petición falla a mitad, el jugador vería cartas que no tiene.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Coins, Gem, Loader2, PackageOpen, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { CardView } from '@/components/cards/CardView';
import { RARITY_STYLES } from '@/components/cards/cardArt';
import { Button } from '@/components/ui/Button';
import { getCardMaster } from '@/data';
import { useOpenPack } from '@/hooks/useCollectionActions';
import { usePackTypes } from '@/hooks/usePackTypes';
import type { CollectionCard, Profile } from '@/types/game';

/** Pausa entre cartas al revelar el sobre. */
const REVEAL_MS = 550;

interface ShopScreenProps {
  profile: Profile | null;
  userId: string | undefined;
  isOffline: boolean;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function ShopScreen({ profile, userId, isOffline }: ShopScreenProps) {
  const packs = usePackTypes();
  const openPack = useOpenPack(userId);
  const [revealed, setRevealed] = useState<CollectionCard[]>([]);
  const [pending, setPending] = useState<CollectionCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isOffline) {
    return (
      <div className="grid flex-1 place-items-center p-6 text-center">
        <p className="max-w-xs text-sm text-slate-400">
          La tienda necesita cuenta. En modo local se juega con el mazo inicial.
        </p>
      </div>
    );
  }

  const buy = (packId: string) => {
    setError(null);
    setRevealed([]);
    setPending(null);

    openPack.mutate(packId, {
      onSuccess: async (cards) => {
        setPending(cards);
        // Se revelan de una en una sobre el resultado que YA devolvió el
        // servidor: la animación no decide nada, solo lo cuenta.
        for (let i = 0; i < cards.length; i += 1) {
          await sleep(REVEAL_MS);
          setRevealed(cards.slice(0, i + 1));
        }
        setPending(null);
      },
      onError: (mutationError) => setError(mutationError.message),
    });
  };

  const isOpening = openPack.isPending || pending !== null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-2 sm:p-4">
      <div className="mx-auto w-full max-w-3xl">
        <h2 className="mb-3 text-lg font-bold text-white">Tienda</h2>

        <AnimatePresence>
          {error ? (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              role="alert"
              className="mb-3 flex items-center gap-1.5 rounded-lg bg-rose-950/70 px-3 py-2 text-xs text-rose-200 ring-1 ring-rose-500/40"
            >
              <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden />
              {error}
            </motion.p>
          ) : null}
        </AnimatePresence>

        {packs.isLoading ? (
          <div className="grid place-items-center py-10 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {(packs.data ?? []).map((pack) => {
              const affordable =
                (pack.priceGold === null || (profile?.gold ?? 0) >= pack.priceGold) &&
                (pack.priceGems === null || (profile?.gems ?? 0) >= pack.priceGems);

              return (
                <div
                  key={pack.id}
                  className="flex flex-col justify-between gap-3 rounded-xl border border-white/10 bg-linear-to-br from-slate-800 to-slate-900 p-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <PackageOpen className="h-5 w-5 text-amber-300" aria-hidden />
                      <h3 className="font-bold text-white">{pack.name}</h3>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">{pack.description}</p>

                    {/* Las probabilidades, a la vista. Esconderlas es lo que
                        convierte un sobre en una tragaperras, y en varios países
                        es directamente ilegal. */}
                    <dl className="mt-3 flex flex-wrap gap-1.5">
                      {Object.entries(pack.rarityWeights).map(([rarity, weight]) => {
                        const total = Object.values(pack.rarityWeights).reduce((a, b) => a + b, 0);
                        const style = RARITY_STYLES[rarity as keyof typeof RARITY_STYLES];
                        return (
                          <span
                            key={rarity}
                            className={`rounded-md bg-slate-950/60 px-1.5 py-0.5 text-[10px] ${style?.text ?? 'text-slate-300'}`}
                          >
                            {style?.label ?? rarity} {((weight / total) * 100).toFixed(0)}%
                          </span>
                        );
                      })}
                    </dl>
                  </div>

                  <Button
                    disabled={!affordable || isOpening}
                    onClick={() => buy(pack.id)}
                    className="w-full"
                  >
                    {pack.priceGold !== null ? (
                      <>
                        <Coins className="h-4 w-4" aria-hidden />
                        {pack.priceGold}
                      </>
                    ) : (
                      <>
                        <Gem className="h-4 w-4" aria-hidden />
                        {pack.priceGems}
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Resultado */}
        {isOpening || revealed.length > 0 ? (
          <section className="mt-6">
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">
              {isOpening ? 'Abriendo…' : 'Te ha tocado'}
            </h3>
            <div className="flex flex-wrap justify-center gap-2">
              <AnimatePresence>
                {revealed.map((card) => {
                  const master = getCardMaster(card.masterId);
                  const rare = master.rarity === 'EPIC' || master.rarity === 'LEGENDARY';
                  return (
                    <motion.div
                      key={card.id}
                      initial={{ opacity: 0, rotateY: 90, scale: 0.7 }}
                      animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 220,
                        damping: 18,
                        // Las buenas tardan un poco más en asentarse: es el
                        // instante que la gente recuerda del sobre.
                        delay: rare ? 0.15 : 0,
                      }}
                      className="w-20 sm:w-24"
                    >
                      <CardView
                        card={{
                          instanceId: card.id,
                          masterId: card.masterId,
                          level: card.level,
                          owner: 'PLAYER',
                        }}
                        variant="hand"
                        highlighted={rare}
                      />
                      {card.serialNumber !== null ? (
                        <p className="mt-0.5 text-center font-mono text-[10px] text-amber-200">
                          #{card.serialNumber}
                        </p>
                      ) : null}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
